import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { config } from "../../config/const";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./FeedbackForm.css";

const SLIDER_OPTIONS = [
  "Strongly disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly agree"
];

const PARENT_QUESTIONS = [
  {
    _id: "68c115cc64d3f85aeacba83a",
    question: "I found the visuals appealing."
  },
  {
    _id: "68c11dc064d3f85aeacba840",
    question: "I would like to share this story with others."
  },
  {
    _id: "68c1164b64d3f85aeacba83e",
    question: "I would like to re-create similar stories."
  }
];

const CHILD_QUESTIONS = [
  {
    _id: "68e390219589a3866ffd1541",
    question: "How closely do you resemble the child in the picture?",
    options: ["Not at all", "A little", "Somewhat", "Quite a bit", "Very much"]
  },
  {
    _id: "68e38fae9589a3866ffd1540",
    question: "I would like to re-create similar stories."
  }
];

const ALL_QUESTIONS = [...PARENT_QUESTIONS, ...CHILD_QUESTIONS];

const FeedbackForm = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { storyData } = location.state || {};
  const [feedbackAnswers, setFeedbackAnswers] = useState(() =>
    Object.fromEntries(
      ALL_QUESTIONS.map(q => [q._id, q.options ? q.options[2] : "Neutral"])
    )
  );
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const handleSliderChange = (questionId, value, options = SLIDER_OPTIONS) => {
    setFeedbackAnswers(ans => ({
      ...ans,
      [questionId]: options[value]
    }));
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    setSubmittingFeedback(true);
    const baseUrl = config?.backend_url || "http://localhost:3002";
    const token = localStorage.getItem("idToken");
    const email = localStorage.getItem("email");
    try {
      const responses = ALL_QUESTIONS.map(q => ({
        feedbackQuestionId: q._id,
        email,
        storyId,
        answer: feedbackAnswers[q._id]
      }));
      await axios.post(`${baseUrl}/user/feedback`, { responses }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      toast.success("Feedback saved! Generating a quiz...");
      const quizResponse = await axios.post(`${baseUrl}/generateQuiz`, {
        content: storyData.content
      });

      navigate(`/generateQuiz/${storyId}`, {
        state: {
          storyId: storyId,
          storyContent: storyData.content,
          title: storyData.title,
          generatedQuiz: quizResponse.data.data
        }
      });

    } catch (error) {
        console.error("Failed to submit feedback:", error);
        toast.error("Couldn't save feedback, please re-try");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <div className="feedback-form-box">
        <h2>Story Feedback</h2>
        <form onSubmit={handleSubmitFeedback}>
          <div className="feedback-section">
            <h3 className="feedback-section-title">For Parents</h3>
            {PARENT_QUESTIONS.map(q => {
              const questionOptions = q.options || SLIDER_OPTIONS;
              return (
                <div key={q._id} className="feedback-question">
                  <p className="feedback-question-text">{q.question}</p>
                  <div className="feedback-slider-row">
                    <input
                      type="range"
                      min={0}
                      max={questionOptions.length - 1}
                      step={1}
                      value={
                        questionOptions.indexOf(feedbackAnswers[q._id]) !== -1
                          ? questionOptions.indexOf(feedbackAnswers[q._id])
                          : Math.floor(questionOptions.length / 2)
                      }
                      onChange={e => handleSliderChange(q._id, Number(e.target.value), questionOptions)}
                      disabled={submittingFeedback}
                      className="feedback-slider"
                    />
                  </div>
                  <div className="feedback-slider-labels">
                    {questionOptions.map((opt, idx) => (
                      <span
                        key={idx}
                        className={`feedback-slider-label${feedbackAnswers[q._id] === opt ? " selected" : ""}`}
                      >
                        {opt}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="feedback-section">
            <h3 className="feedback-section-title">For Children</h3>
            {CHILD_QUESTIONS.map(q => {
              const questionOptions = q.options || SLIDER_OPTIONS;
              return (
                <div key={q._id} className="feedback-question">
                  <p className="feedback-question-text">{q.question}</p>
                  <div className="feedback-slider-row">
                    <input
                      type="range"
                      min={0}
                      max={questionOptions.length - 1}
                      step={1}
                      value={
                        questionOptions.indexOf(feedbackAnswers[q._id]) !== -1
                          ? questionOptions.indexOf(feedbackAnswers[q._id])
                          : Math.floor(questionOptions.length / 2)
                      }
                      onChange={e => handleSliderChange(q._id, Number(e.target.value), questionOptions)}
                      disabled={submittingFeedback}
                      className="feedback-slider"
                    />
                  </div>
                  <div className="feedback-slider-labels">
                    {questionOptions.map((opt, idx) => (
                      <span
                        key={idx}
                        className={`feedback-slider-label${feedbackAnswers[q._id] === opt ? " selected" : ""}`}
                      >
                        {opt}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="submit"
            className="submit-feedback-btn"
            disabled={submittingFeedback}
          >
            {submittingFeedback ? "Submitting..." : "Submit Feedback"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;
